import { memo, useCallback, useRef } from 'react';
import type { ResourceResponse } from 'types';
import { useTypedSelector } from 'hooks';
import { isFormChanged } from 'util/forms';
import { when } from 'util/support';
import {
    selectBankingSdkRecipientPostalAddressTypeOptions,
    selectConstants,
    selectIsConstantsLoading
} from 'features/general/selectors';
import type { BankingSdkRecipient } from 'features/openBanking/types';
import { useBankingSDKRecipientsActions } from 'features/openBanking/actions';
import ActionPanel from 'ui/molecules/ActionPanel';
import When from 'ui/support/When';
import { BankingSDKRecipient } from 'ui/forms/OpenBanking';
import {
    useResourceContext,
    ResourceDetail,
    type ResourceDetailProps
} from "ui/widgets/Resource";

const BankingSDKRecipientsDetail = (props: ResourceDetailProps) => {
    const { open, resource, onClose } = useResourceContext<BankingSdkRecipient>();
    const resourceRef = useRef(resource);
    resourceRef.current = resource;

    return (
        <ResourceDetail
            {...props}
            disableCloseOnBackdropClick
            onClose={onClose}
        >
            <When
                if={open}
                then={useCallback(() => (
                    <BankingSDKRecipientForm
                        resource={resourceRef.current}
                    />
                ), [])}
            >
                <BankingSDKRecipientForm
                    resource={resource}
                />
            </When>
        </ResourceDetail>
    );
};

export default memo(BankingSDKRecipientsDetail);

function BankingSDKRecipientForm({
    resource
}: {
    readonly resource: BankingSdkRecipient;
}) {
    const isConstantsLoading = useTypedSelector(selectIsConstantsLoading);
    const constants = useTypedSelector(selectConstants);

    const { onClose } = useResourceContext<BankingSdkRecipient>();

    const {
        createBankingSdkRecipient,
        updateBankingSdkRecipient
    } = useBankingSDKRecipientsActions();

    const requestHandler = resource
        ? updateBankingSdkRecipient
        : createBankingSdkRecipient;

    const bootstrapData = {
        BankingSdkRecipientEndToEndSlug: constants.BankingSdkRecipientEndToEndSlug,
        addressType: useTypedSelector(selectBankingSdkRecipientPostalAddressTypeOptions)
    };

    const formProps: BankingSDKRecipient.BankingSDKRecipientForm = {
        bootstrapData,
        initialValues: BankingSDKRecipient.getInitialValues(resource, bootstrapData),
        isBootstrapDataLoading: (
            isConstantsLoading
        ),
        onSaveOrCreate: async ({
            addressType,
            country,
            endToEndIdSlug,
            ...values
        }, formHelpers) => {
            const [request, result] = requestHandler({
                ...values,
                endToEndIdSlug: endToEndIdSlug || undefined,
                country: country!.at(0),
                addressType: Number(addressType!.id)
            });

            const response = await request as ResourceResponse<BankingSdkRecipient>;
            when(response.success, () => {
                onClose();
                formHelpers!.resetForm();
            });

            return result;
        }
    };

    const form = BankingSDKRecipient.useForm(formProps);

    return (
        <BankingSDKRecipient.Form
            {...form}
            {...formProps}
        >
            <ActionPanel
                isLoading={form.isSubmitting}
                onSave={async () => { }}
                onCancel={onClose}
                SaveActionProps={{
                    type: 'submit',
                    disabled: !form.isValid || !isFormChanged(form),
                    loading: form.isSubmitting
                }}
            />
        </BankingSDKRecipient.Form>
    );
}
